import Title from 'antd/lib/typography/Title';
import React, { useEffect, useState } from 'react';
import { Tree } from 'antd';
import { Key } from 'antd/lib/table/interface';
import { axiosInstance } from '../../utils/axios';

interface DataNode {
  title: string;
  key: string;
  isLeaf?: boolean;
  children?: DataNode[];
  icon?: React.ReactNode;
  selectable?: boolean;
  documentTitle?: (index: number) => string;
}

const { DirectoryTree } = Tree;

interface UserDocumentsProps {
  onOpenFile: (key: Key[]) => void;
  onExpandGetPaths: (key: string) => Promise<string[]>;
  includedSteps?: string[] | '*';
}

export const defaultOnExpandGetPaths =
  (url: string) =>
  async (key: string): Promise<string[]> =>
    await axiosInstance
      .get(url, {
        params: { check: key },
      })
      .then((response) => {
        if (!response.data) return [];
        if (response.data.constructor.name !== 'Array') return [];
        return response.data;
      });

export const defaultOnOpenFile = (url: string) => (key: Key[]) =>
  axiosInstance.get(url, { params: { path: key[0] } }).then((response) => {
    const fileLink = response.data;
    window.open(fileLink, '_blank')?.focus();
  });

const steps: DataNode[] = [
  {
    title: 'VOG',
    key: 'certificateOfConduct',
    selectable: false,
    documentTitle: (index: number) => `VOG ${index + 1}`,
  },
  {
    title: "Diploma's",
    key: 'education',
    selectable: false,
    documentTitle: (index: number) => `Diploma ${index + 1}`,
  },
];

export const CandidateDocuments = ({
  onOpenFile,
  onExpandGetPaths,
  includedSteps = '*',
}: UserDocumentsProps) => {
  const [treeData, setTreeData] = useState<DataNode[]>([]);

  useEffect(() => {
    setTreeData(
      steps.filter(
        (node: DataNode) =>
          includedSteps === '*' || includedSteps?.includes(node.key),
      ),
    );
  }, [includedSteps]);

  const updateTreeData = (
    list: DataNode[],
    key: React.Key,
    children: DataNode[],
  ): DataNode[] =>
    list.map((node) => {
      if (node.key === key)
        return {
          ...node,
          children,
        };
      if (node.children)
        return {
          ...node,
          children: updateTreeData(node.children, key, children),
        };
      return node;
    });

  const onLoadData = async (node: DataNode) => {
    const paths: string[] = await onExpandGetPaths(node.key);
    const children: DataNode[] = [];
    for (let i: number = 0; i < paths.length; i++)
      children.push({
        title: node.documentTitle ? node.documentTitle(i) : `Bestand ${i + 1}`,
        key: paths[i],
        isLeaf: true,
      });

    if (children.length === 0)
      children.push({
        title: 'Geen bestanden',
        key: '/',
        isLeaf: true,
        icon: <></>,
        selectable: false,
      });

    setTreeData((origin) => updateTreeData(origin, node.key, children));
  };

  if (treeData.length === 0) return null;

  return (
    <>
      <Title level={5}>Kandidaat documenten</Title>
      <DirectoryTree
        loadData={onLoadData}
        treeData={treeData}
        onSelect={onOpenFile}
        selectable={true}
        multiple
      />
    </>
  );
};
